/**
 * Cartao de uma empresa na vista em cartoes da administracao.
 *
 * Decisoes nao obvias:
 *  - Cadastro (`company`) e retorno do periodo (`item`) chegam SEPARADOS: o
 *    cadastro vem de `useCompany().companies` e o retorno da rota. Quando a
 *    rota ainda nao trouxe a empresa, os numeros mostram o marcador de
 *    ausencia -- nunca zero, que seria uma afirmacao falsa sobre a empresa.
 *  - A saude fica ao lado do nome e o motivo principal numa linha propria,
 *    com o botao que leva a aba onde se resolve: e o que a equipe precisa
 *    saber antes de qualquer numero.
 *  - "Abrir CRM" e o `primary` do cartao (e a acao que a equipe mais faz);
 *    "Detalhes" e contorno. Um cheio por bloco.
 *  - Provedor e funil sao selos com texto; comissao e cobranca numa linha so.
 */

import { Link } from 'react-router-dom';
import { Sparkline } from '../../../components/Sparkline';
import { Badge, Button, CompanyLogo, Icon, LinkButton, Panel } from '../../../components/ui';
import { VALOR_AUSENTE, formatBRLFromCents, formatCount, formatDate, formatPercentFromBps } from '../../../lib/format';
import type { LinhaEmpresa } from '../lista';
import { achadoPrincipal } from '../saude';
import type { AbaDetalhe } from '../types';
import { FUNIL_INFO, MODELO_COBRANCA, PROVEDOR_INFO, STATUS_EMPRESA } from '../vocabulario';
import { Delta } from './Delta';
import { SeloDeSaude } from './SaudeEmpresa';

export function CartaoEmpresa({
  linha,
  onAbrirCrm,
  linkDe,
}: {
  readonly linha: LinhaEmpresa;
  readonly onAbrirCrm: (companyId: string) => void;
  readonly linkDe: (companyId: string, aba?: AbaDetalhe) => string;
}): JSX.Element {
  const { company, item, saude } = linha;
  const status = STATUS_EMPRESA[company.status];
  const modelo = MODELO_COBRANCA[company.billingModel];
  const tituloId = `empresa-${company.slug}-titulo`;
  const m = item?.metrics ?? null;
  const anterior = item?.previous ?? null;
  const motivo = saude === null ? null : achadoPrincipal(saude);
  const semanas = item?.weeklyRecoveredCents ?? [];

  return (
    <li>
      <Panel className="flex h-full flex-col p-5">
        <article aria-labelledby={tituloId} className="flex h-full flex-col gap-4">
          <header className="flex items-start gap-3">
            <CompanyLogo company={company} size="lg" />
            <div className="min-w-0 flex-1">
              <h3 id={tituloId} className="truncate text-base font-semibold text-ink">
                <Link to={linkDe(company.id)} className="hover:text-brand">
                  {company.name}
                </Link>
              </h3>
              <p className="truncate text-sm text-ink-muted">{company.segment === '' ? 'Segmento não informado' : company.segment}</p>
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                <Badge tone={status.tone} dot>
                  {status.label}
                </Badge>
                <SeloDeSaude saude={saude} />
              </div>
            </div>
          </header>

          {motivo === null ? null : (
            <Link
              to={linkDe(company.id, motivo.aba ?? 'resumo')}
              className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-xs transition-colors hover:border-brand/60 ${
                motivo.tom === 'critical' ? 'border-status-critical/40 bg-status-critical/5' : 'border-status-warning/50 bg-status-warning/5'
              }`}
            >
              <Icon name="alerta" size="xs" className={`mt-0.5 ${motivo.tom === 'critical' ? 'text-status-critical' : 'text-status-warning'}`} />
              <span className="text-ink">{motivo.texto}</span>
            </Link>
          )}

          <div className="flex items-end justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle" title="Valor pago pelos leads que a régua trouxe de volta no período">
                Recuperado no período
              </p>
              <p className="mt-0.5 text-2xl font-semibold tracking-tight text-ink">{m === null ? VALOR_AUSENTE : formatBRLFromCents(m.recoveredAmountCents)}</p>
              {m === null ? null : <Delta atual={m.recoveredAmountCents} anterior={anterior === null ? null : anterior.recoveredAmountCents} tipo="dinheiro" />}
            </div>
            {semanas.length >= 2 ? <Sparkline values={semanas} label={`Recuperado por semana de ${company.name}`} className="h-10 w-24 shrink-0" /> : null}
          </div>

          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
            <div>
              <dt className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Comissão</dt>
              <dd className="mt-0.5 tabular-nums text-ink">
                {m === null ? VALOR_AUSENTE : formatBRLFromCents(m.commissionCents)}{' '}
                <span className="text-xs text-ink-subtle" title="Percentual atual da empresa. A comissão ao lado já foi apurada e fica congelada no percentual de cada venda.">
                  · {formatPercentFromBps(company.feeBps)}
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Vendas · taxa</dt>
              <dd className="mt-0.5 tabular-nums text-ink">
                {m === null ? VALOR_AUSENTE : formatCount(m.recoveredCount)}{' '}
                <span className="text-xs text-ink-subtle">· {m === null || m.abandonedCount === 0 ? VALOR_AUSENTE : formatPercentFromBps(m.recoveryRateBps)}</span>
              </dd>
            </div>
            <div>
              <dt className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Cobrança</dt>
              <dd className="mt-0.5">
                <Badge tone={modelo.tone}>{modelo.curto}</Badge>
              </dd>
            </div>
            <div>
              <dt className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Funis ativos</dt>
              <dd className="mt-0.5 text-ink">
                {company.funnels.length === 0 ? 'Nenhum' : company.funnels.map((f) => FUNIL_INFO[f].label).join(' · ')}
                {item === null ? null : (
                  <span className="text-ink-subtle">
                    {' '}
                    · {formatCount(item.activeOffers)} {item.activeOffers === 1 ? 'oferta ativa' : 'ofertas ativas'}
                  </span>
                )}
              </dd>
            </div>
          </dl>

          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Provedores</span>
            {linha.provedores.length === 0 ? (
              <Badge tone="warning" dot>
                Nenhum conectado
              </Badge>
            ) : (
              linha.provedores.map((provider) => (
                <Badge key={provider} tone="neutral" icon="link">
                  {PROVEDOR_INFO[provider].curto}
                </Badge>
              ))
            )}
          </div>

          <p className="text-xs text-ink-subtle">
            Criada em {formatDate(company.createdAt)} · contato {company.contactName === '' ? VALOR_AUSENTE : company.contactName}
          </p>

          <div className="mt-auto flex flex-wrap items-center gap-2 pt-1">
            <Button variant="primary" icon="painel" onClick={() => onAbrirCrm(company.id)}>
              Abrir CRM<span className="sr-only"> de {company.name}</span>
            </Button>
            <LinkButton to={linkDe(company.id)} variant="secondary" iconRight="seta-dir" keepSearch={false}>
              Detalhes<span className="sr-only"> de {company.name}</span>
            </LinkButton>
          </div>
        </article>
      </Panel>
    </li>
  );
}
